import * as React from "react";
import type { AppProps } from "next/app";
import { useRouter } from "next/router";

import { ScreenProvider } from "@foundation/screen";
import type { BreakpointsConfig } from "@foundation/screen/lib/types";
import { PersonalizationProvider } from "@foundation/personalization";

import { IntlProvider } from "react-intl";
import { getLanguageCodeLocale, Locale } from "~constants/i18n";

import "@bloomberg/intercom/src/components/styles/variables.css";
import "~styles/global.css";

// eslint-disable-next-line @typescript-eslint/no-var-requires
const regUIClientCSS: string = require("@foundation/reg-ui-client-css");

const PERSONALIZATION_URL = process.env.NEXT_PUBLIC_PERSONALIZATION_URL ?? "";

export default function NextApp(props: AppProps<Record<string, any>>) {
  const { Component, pageProps } = props;
  const { locale, defaultLocale } = useRouter();

  const languageCodeLocale = getLanguageCodeLocale(locale);

  // Router locales mirror Lineup: 'en' | 'ja' | 'ja-JP' note: they will remove ja-JP at some point
  const resolvedLocale = languageCodeLocale ?? defaultLocale ?? "en";

  const messagesLocale = resolvedLocale.startsWith("ja")
    ? "ja"
    : resolvedLocale;

  // Handle either a flat messages object OR a map keyed by locale
  const rawMessages = pageProps?.messages;
  const messages = (rawMessages?.[messagesLocale] ??
    rawMessages ??
    {}) as Record<string, string>;

  const breakpoints: BreakpointsConfig = {
    tablet: 768,
    extraSmallDesktop: 912,
    smallDesktop: 1123,
    largeDesktop: 1335,
  };

  return (
    <>
      <style jsx global>
        {regUIClientCSS}
      </style>
      <PersonalizationProvider
        application="bazaar"
        clientUrl={PERSONALIZATION_URL}
      >
        <IntlProvider
          // Keep full BCP-47 for formatting (e.g., 'ja-JP')
          locale={resolvedLocale}
          defaultLocale={Locale.En}
          messages={messages}
        >
          <ScreenProvider breakpoints={breakpoints}>
            <Component {...pageProps} />
          </ScreenProvider>
        </IntlProvider>
      </PersonalizationProvider>
    </>
  );
}
